import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { useTranslation } from 'react-i18next';
import { handleError, validateEmail, validateNonEmpty, removeNullsAndEmptys, fetchRegions } from "../../utils/lib";
import { TextField, Autocomplete } from "@mui/material";
import axios from "axios";
import EditPage from "./EditPage";

const EditProvider = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const {updateProvider} = location.state || {};

  const [provider, setProvider] = useState(
    updateProvider || {
      name: "",
      address: "",
      region: null,
      phone: "",
      email: "",
      web_page: "",
      regional_id: "",
      notes: "",
    }
  );

  const [regions, setRegions] = useState([]);

  const [errors, setErrors] = useState({});
  const [refs] = useState({
    name: useRef(null),
    address: useRef(null),
    region: useRef(null),
    phone: useRef(null),
    email: useRef(null),
    regional_id: useRef(null),
    web_page: useRef(null),
    notes: useRef(null),
  });

  useEffect(() => {
    updateTitle(
      updateProvider ? updateProvider.thumb_url : '',
      updateProvider ? t('edit.editProvider') : t('edit.newProvider'),
      t('common.completeData')
    );

    toggleFab(false);

    const fetchData = async () => {
      try {
        const regions = await fetchRegions('regions');
        setRegions(regions);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);

        // Add a delay to focus to ensure component is fully rendered
        setTimeout(() => {
          if (refs.name.current) {
            refs.name.current.focus();
          }
        }, 100);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProvider({ ...provider, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !validateNonEmpty("name", provider.name, setErrors, refs.name) ||
      !validateNonEmpty("region", provider.region?.friendly_name, setErrors, refs.region) ||
      !validateEmail("email", provider.email, setErrors, refs.email, false)
    )
      return;

    save();
  };

  const save = async () => {
    setIsLoading(true);
    try {
      let response;
      if (updateProvider) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}products_providers`,
          removeNullsAndEmptys(provider),
          { params: { id: updateProvider.id }, withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}products_providers`,
          removeNullsAndEmptys(provider),
          { withCredentials: true }
        );
      }

      navigate('/main/view-provider', { state: { id: response.data.data.id, updateLastView: false } });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['name', 'address', 'region', 'phone', 'email', 'web_page', 'notes'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <TextField
        fullWidth
        margin="normal"
        label={t('common.name') + " *"}
        name="name"
        value={provider.name}
        onChange={handleChange}
        inputRef={refs.name}
        error={!!errors.name}
        helperText={errors.name}
        onKeyDown={(e) => handleKeyDown(e, 'name')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.address')}
        name="address"
        value={provider.address}
        onChange={handleChange}
        inputRef={refs.address}
        onKeyDown={(e) => handleKeyDown(e, 'address')}
      />
      <Autocomplete
        fullWidth
        options={regions}
        getOptionLabel={(option) => (option ? option.friendly_name : "")}
        value={provider.region}
        onChange={(e, newValue) =>
          setProvider({
            ...provider,
            region: newValue,
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.region')}
            name="region"
            margin="normal"
            inputRef={refs.region}
            error={!!errors.region}
            helperText={errors.region}
            onKeyDown={(e) => handleKeyDown(e, 'region')}
          />
        )}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.phone')}
        name="phone"
        value={provider.phone}
        onChange={handleChange}
        inputRef={refs.phone}
        onKeyDown={(e) => handleKeyDown(e, 'phone')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.email')}
        name="email"
        value={provider.email}
        onChange={handleChange}
        error={!!errors.email}
        helperText={errors.email}
        inputRef={refs.email}
        onKeyDown={(e) => handleKeyDown(e, 'email')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.webPage')}
        name="web_page"
        value={provider.web_page}
        onChange={handleChange}
        inputRef={refs.web_page}
        onKeyDown={(e) => handleKeyDown(e, 'web_page')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.notes')}
        name="notes"
        value={provider.notes}
        onChange={handleChange}
        multiline
        rows={4}
        inputRef={refs.notes}
        onKeyDown={(e) => handleKeyDown(e, 'notes')}
      />
    </EditPage>
  );
};

export default EditProvider;
